import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { encountersApi, encounterQueryKeys } from "@/shared/api/encounters";
import { apiError } from "@/shared/api/client";
import { formatVnd } from "@/shared/format";
import { DepartmentSelect } from "@/shared/ui/DepartmentSelect";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Chuyển khoa — creates a fresh waiting entry for the patient at the target department
 * (TD-7b, #18800).
 *
 * <p><b>Owns all of its own state.</b> The target department, the reason, the pending flag and the
 * success toast never existed anywhere but here; the workspace held them only because everything
 * was in one file. It takes the encounter id and two callbacks, not a bag of setters — §5 says
 * state ownership matters more than the file split, and a dialog that needed the parent's state
 * would have been a rename rather than a decoupling.
 *
 * <p>The one thing it does NOT own is the error message. Errors surface in the workspace's single
 * error line, because a doctor should not have to hunt for which panel a failure came from.
 */
export function TransferDepartmentDialog({
  encounterId,
  open,
  onOpenChange,
  onError,
}: {
  encounterId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Reported to the workspace's shared error line rather than shown inside the dialog. */
  onError: (message: string) => void;
}) {
  const qc = useQueryClient();
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [doneMessage, setDoneMessage] = useState<string | null>(null);

  const transfer = useMutation({
    mutationFn: () =>
      encountersApi.transferDepartment(encounterId, {
        departmentId: departmentId as string,
        reason: reason.trim() || null,
      }),
    onSuccess: (res) => {
      onOpenChange(false);
      setReason("");
      qc.invalidateQueries({ queryKey: encounterQueryKeys.queue() });
      const fee = res.examFeeAmount > 0 ? formatVnd(res.examFeeAmount) : "miễn/BHYT";
      setDoneMessage(
        `Đã chuyển bệnh nhân sang khoa ${res.departmentName} — đã vào hàng chờ khám (phí khám: ${fee}).`
      );
    },
    onError: (e) => onError(apiError(e).detail),
  });

  return (
    <>
      {doneMessage && (
        <div
          className="fixed bottom-20 right-5 z-[60] max-w-md cursor-pointer rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-md"
          onClick={() => setDoneMessage(null)}
          title="Bấm để đóng"
        >
          {doneMessage}
        </div>
      )}

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) onOpenChange(false);
        }}
      >
        <DialogContent aria-label="Chuyển khoa">
          <DialogHeader>
            <DialogTitle>Chuyển khoa</DialogTitle>
            <DialogDescription>
              Tạo một lượt chờ khám mới cho bệnh nhân ở khoa đích. Lần khám hiện tại đã ký sẽ được
              giữ nguyên.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Khoa chuyển đến</Label>
            <DepartmentSelect value={departmentId} onChange={setDepartmentId} />
          </div>
          <div className="space-y-1.5">
            <Label>Lý do (không bắt buộc)</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(ev) => setReason(ev.target.value)}
              placeholder="VD: cần khám chuyên khoa Tai Mũi Họng…"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Huỷ
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!departmentId || transfer.isPending}
              onClick={() => transfer.mutate()}
            >
              {transfer.isPending ? "Đang chuyển…" : "Chuyển khoa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Clears the panel's own toast when the workspace re-opens the dialog. */
export type TransferDepartmentDialogProps = Parameters<typeof TransferDepartmentDialog>[0];
